import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import KeyIcon from '@mui/icons-material/Key';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import { motion } from 'motion/react';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import DigipinDisclaimer from '../site/DigipinDisclaimer';
import Footer from '../Footer';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgSurface: '#0F172A',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
  greenSignal: '#10B981',
};

const STEPS = [
  {
    n: '1',
    title: 'Install QGIS Plugin',
    body: 'Install DIGIPIN Encoder Pro 2.0 into QGIS 3.16+ from the GeoVera repository.',
  },
  {
    n: '2',
    title: 'Request API Key',
    body: 'Sign in to GeoVera, navigate to Developers → DIGIPIN, and claim your approved key.',
  },
  {
    n: '3',
    title: 'Paste Key in Plugin',
    body: 'Enter your gv_dp_live_… key into QGIS settings to enable Online high-speed mode.',
  },
  {
    n: '4',
    title: 'Encode & Batch Layers',
    body: 'Click map points to derive 10-char DIGIPIN codes or process entire layer attribute tables.',
  },
] as const;

const FEATURES = [
  {
    icon: <PinDropOutlinedIcon sx={{ color: TOKENS.accent, fontSize: 28 }} />,
    title: 'Map Click Encoder',
    body: 'One click on any QGIS canvas location generates a 10-character India Post DIGIPIN grid code.',
  },
  {
    icon: <MapOutlinedIcon sx={{ color: TOKENS.secondary, fontSize: 28 }} />,
    title: 'Decode & Auto-Zoom',
    body: 'Paste any DIGIPIN code to zoom to the exact location and draw the ~4m × 4m grid cell bounding box.',
  },
  {
    icon: <LayersOutlinedIcon sx={{ color: TOKENS.greenSignal, fontSize: 28 }} />,
    title: 'Batch Layer Attributes',
    body: 'Append DIGIPIN codes to point, line, and polygon layers automatically or export directly to CSV.',
  },
  {
    icon: <KeyIcon sx={{ color: '#EAB308', fontSize: 28 }} />,
    title: 'Cloud Key Management',
    body: 'Personal, admin-approved keys managed directly from your GeoVera platform account.',
  },
  {
    icon: <SpeedIcon sx={{ color: '#A855F7', fontSize: 28 }} />,
    title: 'Online + Offline Hybrid',
    body: 'Use GeoVera Cloud API for high-throughput batch encoding, or switch to local offline engine.',
  },
] as const;

const DigipinEncoderLandingPage = () => {
  return (
    <Box sx={{ ...publicPageSx, bgcolor: TOKENS.bgDark, color: TOKENS.textPrimary }}>
      <PublicSiteChrome active="digipin" subtitle="DIGIPIN 4m Geo-Grid" />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Button
          component={RouterLink}
          to="/qgis-plugins"
          size="small"
          sx={{
            mb: 3,
            color: TOKENS.textSecondary,
            textTransform: 'none',
            fontWeight: 600,
            px: 0,
            '&:hover': { color: TOKENS.textHighlight, bgcolor: 'transparent' },
          }}
        >
          ← All QGIS Plugins
        </Button>

        {/* HERO SECTION */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' },
            gap: 6,
            alignItems: 'center',
            mb: 10,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip
                label="NATIONAL GEO-SPATIAL GRID STANDARD"
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.18)',
                  color: TOKENS.accent,
                  border: `1px solid ${TOKENS.accentGlow}`,
                  fontWeight: 800,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                }}
              />
              <Chip
                label="QGIS 3.16+"
                size="small"
                sx={{
                  bgcolor: 'rgba(59, 130, 246, 0.15)',
                  color: TOKENS.textHighlight,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4rem' },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.08,
                mb: 2.5,
              }}
            >
              DIGIPIN Encoder Pro for QGIS
            </Typography>

            <Typography
              sx={{ color: TOKENS.textSecondary, fontSize: '1.15rem', lineHeight: 1.6, mb: 2 }}
            >
              India Post&apos;s 4m × 4m national geo-spatial pinning standard — available in QGIS
              via GeoVera. Encode coordinates, decode DIGIPIN codes, and process layer attribute
              tables with high precision. GeoVera is not an official India Post product.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={RouterLink}
                to="/developers/digipin"
                variant="contained"
                size="large"
                startIcon={<KeyIcon />}
                sx={{
                  bgcolor: TOKENS.accent,
                  color: '#FFF',
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: `0 8px 24px ${TOKENS.accentGlow}`,
                  '&:hover': { bgcolor: '#B45309' },
                }}
              >
                Get Free API Key
              </Button>

              <Button
                component="a"
                href="https://github.com/landvera/geovera"
                target="_blank"
                variant="outlined"
                size="large"
                startIcon={<DownloadIcon />}
                sx={{
                  borderColor: TOKENS.border,
                  color: TOKENS.textPrimary,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': { borderColor: TOKENS.secondary, bgcolor: 'rgba(59, 130, 246, 0.1)' },
                }}
              >
                Download QGIS Zip
              </Button>
            </Stack>

            <Box sx={{ mt: 3, maxWidth: 560 }}>
              <DigipinDisclaimer variant="banner" />
            </Box>
          </motion.div>

          {/* Interactive Code / Grid Preview Card */}
          <Paper
            sx={{
              p: 4,
              bgcolor: TOKENS.bgCard,
              border: `1px solid ${TOKENS.borderActive}`,
              borderRadius: 3,
              boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px ${TOKENS.accentGlow}`,
            }}
          >
            <Stack spacing={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    color: TOKENS.textHighlight,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  LIVE DIGIPIN ENCODER SIMULATOR
                </Typography>
                <Chip
                  label="ONLINE 60 FPS"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.2)',
                    color: TOKENS.greenSignal,
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <Box
                sx={{
                  bgcolor: 'rgba(11, 19, 43, 0.9)',
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${TOKENS.border}`,
                  fontFamily: 'monospace',
                  fontSize: 13,
                }}
              >
                <Typography sx={{ color: TOKENS.textSecondary, mb: 1, fontSize: 11 }}>
                  # Input Coordinates (WGS84)
                </Typography>
                <Typography sx={{ color: '#F8FAFC', mb: 2 }}>
                  Latitude: 18.520432° N · Longitude: 73.856741° E
                </Typography>
                <Typography sx={{ color: TOKENS.textSecondary, mb: 1, fontSize: 11 }}>
                  # Output India Post DIGIPIN Grid Code
                </Typography>
                <Typography
                  sx={{
                    color: TOKENS.accent,
                    fontSize: 20,
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                  }}
                >
                  432-856-7814
                </Typography>
                <Typography sx={{ color: TOKENS.greenSignal, mt: 1, fontSize: 11 }}>
                  ✓ Grid Bounding Box: 4.0m × 4.0m Resolution
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* FEATURES GRID */}
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}>
          Core QGIS Plugin Features
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            mb: 10,
          }}
        >
          {FEATURES.map((f) => (
            <Paper
              key={f.title}
              sx={{
                p: 3.5,
                bgcolor: 'rgba(15, 23, 42, 0.65)',
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 1.25,
                    bgcolor: 'rgba(30, 58, 138, 0.3)',
                    borderRadius: 2,
                    width: 'fit-content',
                  }}
                >
                  {f.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {f.title}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.9rem', color: TOKENS.textSecondary, lineHeight: 1.6 }}
                >
                  {f.body}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>

        {/* STEP BY STEP INSTALLATION */}
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}>
          Quick 4-Step Setup Guide
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          {STEPS.map((s) => (
            <Paper
              key={s.n}
              sx={{
                p: 3.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: TOKENS.accent,
                    color: '#FFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {s.n}
                </Box>
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {s.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.9rem', color: TOKENS.textSecondary, lineHeight: 1.6 }}
                  >
                    {s.body}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default DigipinEncoderLandingPage;
